import Tooltip from '@mui/material/Tooltip';
import type { ReactElement } from 'react';
import type { HexaNodeDto } from '../../lib/characterProgress';
import { commonCoreSlug } from './commonCore';
import { resolveFormula, useHexaSkillInfo } from '../../lib/hexaSkillInfo';

/** The vendored-info key for a node: its resolved skill, or the hardcoded common-core pair
 *  (Sol Janus=0 / Sol Hecate=1 — same convention as `HexaSection`'s `nodeLabel`, both now sourced
 *  from the shared `commonCoreSlug` helper — REV22 Fix 1). */
function skillInfoSlug(node: HexaNodeDto): string | null {
  if (node.skillSlug) return node.skillSlug;
  if (node.nodeType === 'common_core') return commonCoreSlug(node.nodeIndex);
  return null;
}

/**
 * Hover/focus/long-press tooltip for a HEXA skill icon: what the skill does *at the node's set
 * level*. Effect lines come from the vendored wiki formulas (`lib/hexaSkillInfo.ts` — level
 * expressions resolved client-side; unsupported wiki notation renders `?`), followed by the static
 * description/milestone text. A level-0 node previews Lv. 1 (a level-0 formula readout would be
 * nonsense — the skill isn't learned) and says so. Nodes with no vendored entry (the Boost
 * enhancement nodes have no wiki pages — see `scripts/fetch-hexa-skill-info.mjs`'s KNOWN GAP note)
 * fall back to a plain name + level/goal tooltip rather than rendering nothing.
 *
 * <p>REV22 Fix 3 (docs/specs/REV22-hexa-stat-and-merged-masteries.md): `slugOverride` lets a caller
 * look up a specific skill's wiki info instead of the node's own resolved (primary) one — used for
 * a merged Mastery Core's per-skill icon strip, where each icon needs its own skill's tooltip even
 * though they all share one node's level/goal.
 */
export function HexaSkillTooltip({
  node,
  label,
  slugOverride,
  children,
}: {
  node: HexaNodeDto;
  label: string;
  slugOverride?: string | undefined;
  children: ReactElement;
}) {
  const infoQuery = useHexaSkillInfo();
  const slug = slugOverride ?? skillInfoSlug(node);
  const info = slug !== null ? infoQuery.data?.skills[slug] : undefined;
  const previewLevel = Math.max(1, node.level);

  const title = info ? (
    <div className="space-y-1.5">
      <p className="flex items-baseline justify-between gap-3 text-[0.8125rem] font-semibold text-text">
        <span>{label}</span>
        <span className="shrink-0 text-xs font-medium text-text-faint">
          Lv. {node.level}/{node.targetLevel}
          {node.level === 0 ? ' · showing Lv. 1' : ''}
        </span>
      </p>
      {info.formula && (
        <p className="whitespace-pre-line text-xs leading-relaxed text-text-muted">
          {resolveFormula(info.formula, previewLevel)}
        </p>
      )}
      {info.description && (
        <p className="whitespace-pre-line border-t border-border pt-1.5 text-[0.6875rem] leading-relaxed text-text-faint">
          {info.description}
        </p>
      )}
    </div>
  ) : (
    `${label} — Lv. ${node.level}/${node.targetLevel}`
  );

  return (
    <Tooltip
      title={title}
      enterTouchDelay={150}
      leaveTouchDelay={4000}
      slotProps={{
        tooltip: {
          sx: {
            maxWidth: 340,
            backgroundColor: 'var(--bg-elevated)',
            border: '1px solid var(--border-strong)',
            borderRadius: '0.75rem',
            boxShadow: '0 8px 24px rgb(0 0 0 / 0.4)',
            padding: '0.625rem 0.75rem',
          },
        },
      }}
    >
      <span className="inline-flex">{children}</span>
    </Tooltip>
  );
}
